import type {
  Piq,
  WatResponseItem,
  SrtResponseItem,
  SrtSituation,
  TatCard,
  TatResponseItem,
  PpdtResponseItem,
  GpeResponseItem,
  GdResponseItem,
  LecturetteResponseItem,
  OirQuestion,
  OirResponseItem,
  OirReport,
  SdtTopic,
  SdtResponseItem,
  AssessmentReport,
  AskAnythingMessage,
  InterviewMessage,
} from './types'

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export const api = {
  me: () => fetch('/api/auth/me'),
  logout: () => fetch('/api/auth/logout', { method: 'POST' }).catch(() => {}),

  samplePiq: () => fetch('/api/sample-piq').then((r) => json<Piq>(r)),
  submitPiq: (piq: Piq) =>
    fetch('/api/piq', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(piq),
    }),

  wordbank: (demo: boolean) =>
    fetch(`/api/wordbank?demo=${demo}`).then((r) => json<{ words: string[]; seconds_per_word: number }>(r)),
  submitWat: (responses: WatResponseItem[]) =>
    fetch('/api/wat/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses }),
    }).then((r) => json<AssessmentReport>(r)),
  watReport: () => fetch('/api/report').then((r) => json<AssessmentReport>(r)),

  srtSituations: (demo: boolean) =>
    fetch(`/api/srt-situations?demo=${demo}`).then((r) =>
      json<{ situations: SrtSituation[]; seconds_per_situation: number }>(r)
    ),
  submitSrt: (responses: SrtResponseItem[]) =>
    fetch('/api/srt/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses }),
    }).then((r) => json<AssessmentReport>(r)),
  srtReport: () => fetch('/api/srt/report').then((r) => json<AssessmentReport>(r)),

  tatCards: (gender: 'male' | 'female', demo: boolean) =>
    fetch(`/api/tat-cards?gender=${gender}&demo=${demo}`).then((r) =>
      json<{ cards: TatCard[]; seconds_view: number; seconds_write: number }>(r)
    ),
  submitTat: (responses: TatResponseItem[]) =>
    fetch('/api/tat/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses }),
    }).then((r) => json<AssessmentReport>(r)),
  tatReport: () => fetch('/api/tat/report').then((r) => json<AssessmentReport>(r)),

  ppdtPicture: () =>
    fetch('/api/ppdt-picture').then((r) =>
      json<{ image_url: string; seconds_view: number; seconds_formulate: number; seconds_write: number }>(r)
    ),
  submitPpdt: (response: PpdtResponseItem) =>
    fetch('/api/ppdt/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(response),
    }).then((r) => json<AssessmentReport>(r)),
  ppdtReport: () => fetch('/api/ppdt/report').then((r) => json<AssessmentReport>(r)),

  gpeScenario: () =>
    fetch('/api/gpe-scenario').then((r) =>
      json<{ model_image_url: string; model_description: string; situation: string; seconds_study: number; seconds_write: number }>(r)
    ),
  submitGpe: (response: GpeResponseItem) =>
    fetch('/api/gpe/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(response),
    }).then((r) => json<AssessmentReport>(r)),
  gpeReport: () => fetch('/api/gpe/report').then((r) => json<AssessmentReport>(r)),

  gdTopic: () =>
    fetch('/api/gd-topic').then((r) =>
      json<{ topic_id: string; topic: string; leads: string[]; seconds_write: number }>(r)
    ),
  submitGd: (response: GdResponseItem) =>
    fetch('/api/gd/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(response),
    }).then((r) => json<AssessmentReport>(r)),
  gdReport: () => fetch('/api/gd/report').then((r) => json<AssessmentReport>(r)),

  lecturetteTopics: () =>
    fetch('/api/lecturette-topics').then((r) =>
      json<{ topics: string[]; seconds_prepare: number; seconds_write: number }>(r)
    ),
  submitLecturette: (response: LecturetteResponseItem) =>
    fetch('/api/lecturette/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(response),
    }).then((r) => json<AssessmentReport>(r)),
  lecturetteReport: () => fetch('/api/lecturette/report').then((r) => json<AssessmentReport>(r)),

  oirQuestions: (demo: boolean) =>
    fetch(`/api/oir-questions?demo=${demo}`).then((r) =>
      json<{ questions: OirQuestion[]; seconds_per_question: number; total_seconds: number }>(r)
    ),
  submitOir: (responses: OirResponseItem[], timeTakenSeconds: number) =>
    fetch('/api/oir/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses, time_taken_seconds: timeTakenSeconds }),
    }).then((r) => json<OirReport>(r)),
  oirReport: () => fetch('/api/oir/report').then((r) => json<OirReport>(r)),

  sdtTopics: () =>
    fetch('/api/sdt-topics').then((r) => json<{ topics: SdtTopic[]; seconds_per_topic: number }>(r)),
  submitSdt: (responses: SdtResponseItem[]) =>
    fetch('/api/sdt/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses }),
    }).then((r) => json<AssessmentReport>(r)),
  sdtReport: () => fetch('/api/sdt/report').then((r) => json<AssessmentReport>(r)),

  askAnything: (message: string, history: AskAnythingMessage[]) =>
    fetch('/api/ask-anything', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, history }),
    }).then((r) => json<{ answer: string }>(r)),

  startInterview: () =>
    fetch('/api/interview/start', { method: 'POST' }).then((r) => json<{ reply: string }>(r)),
  interviewReply: (history: InterviewMessage[], message: string, minutesElapsed: number) =>
    fetch('/api/interview/reply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ history, message, minutes_elapsed: minutesElapsed }),
    }).then((r) => json<{ reply: string; is_final: boolean }>(r)),
  submitInterview: (transcript: InterviewMessage[]) =>
    fetch('/api/interview/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript }),
    }).then((r) => json<AssessmentReport>(r)),
  interviewReport: () => fetch('/api/interview/report').then((r) => json<AssessmentReport>(r)),

  // BSF Module: a separate, short practice run of the psych battery. Each finished run is
  // saved as its own history entry (bsfSaveRun / bsfRuns), apart from the real test reports.
  bsfTatCards: (gender: 'male' | 'female') =>
    fetch(`/api/bsf/tat-cards?gender=${gender}`).then((r) =>
      json<{ cards: TatCard[]; seconds_view: number; seconds_write: number }>(r)
    ),
  bsfWordbank: () => fetch('/api/bsf/wordbank').then((r) => json<{ words: string[]; seconds_per_word: number }>(r)),
  bsfSrtSituations: () =>
    fetch('/api/bsf/srt-situations').then((r) =>
      json<{ situations: SrtSituation[]; seconds_per_situation: number }>(r)
    ),
  bsfSdtTopics: () => fetch('/api/bsf/sdt-topics').then((r) => json<{ topics: SdtTopic[]; seconds_per_topic: number }>(r)),
  bsfSubmit: (payload: {
    tat_responses: TatResponseItem[]
    wat_responses: WatResponseItem[]
    srt_responses: SrtResponseItem[]
    sdt_responses: SdtResponseItem[]
  }) =>
    fetch('/api/bsf/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) =>
      json<{
        tat_report: AssessmentReport
        wat_report: AssessmentReport
        srt_report: AssessmentReport
        sdt_report: AssessmentReport
      }>(r)
    ),
  bsfSubmitStage: (
    stage: 'tat' | 'wat' | 'srt' | 'sdt',
    responses: TatResponseItem[] | WatResponseItem[] | SrtResponseItem[] | SdtResponseItem[]
  ) =>
    fetch('/api/bsf/submit-stage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage, [`${stage}_responses`]: responses }),
    }).then((r) => json<AssessmentReport>(r)),
  bsfSaveRun: (payload: {
    gender: 'male' | 'female'
    tat_responses: TatResponseItem[]
    wat_responses: WatResponseItem[]
    srt_responses: SrtResponseItem[]
    sdt_responses: SdtResponseItem[]
    tat_report: AssessmentReport
    wat_report: AssessmentReport
    srt_report: AssessmentReport
    sdt_report: AssessmentReport
  }) =>
    fetch('/api/bsf/runs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => json<{ id: string; created_at: string }>(r)),
  bsfRuns: () =>
    fetch('/api/bsf/runs').then((r) =>
      json<{ runs: { id: string; gender: string; created_at: string; reports: Record<'tat' | 'wat' | 'srt' | 'sdt', AssessmentReport> }[] }>(r)
    ),
}
